import { useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

import ConfirmPopup from '../../Popups/ConfirmPopup'
import {
  createShopSKU,
  deleteShopSKU,
  selectShopSKUById,
  updateShopSKU,
} from '../../../reducers/Organisation/organisationShopReducer'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'

const OrganisationSKUModal = ({ parentCategory, onModalClose }) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { skuId, productId, serviceId } = useParams()
  const dispatch = useDispatch()

  // Redux selector for fetching SKU data
  const sku = useSelector((state) => selectShopSKUById(state)(skuId))

  // State management
  const [formPending, setFormPending] = useState(false)
  const [modalConfirmOpened, setModalConfirmOpened] = useState(false)
  const [formError, setFormError] = useState('')

  // Determine Parent ID
  const getParentId = useCallback(
    () => (parentCategory === 'Product' ? productId : serviceId),
    [parentCategory, productId, serviceId],
  )

  const isNew = skuId === 'new'

  const closeModal = () => {
    onModalClose()
    setFormPending(false)
    setModalConfirmOpened(false)
  }

  const openConfirmModal = (e) => {
    e.preventDefault()
    setModalConfirmOpened(true)
  }

  const closeConfirmModal = () => {
    setModalConfirmOpened(false)
  }

  const cancelServiceChanges = (e) => {
    e.preventDefault()
    closeModal()
  }

  const saveSKU = async ({ id, name, cost, duration, quantity }) => {
    const SKUData = {
      name,
      cost,
      productId: getParentId(),
    }

    if (duration !== '') SKUData.duration = duration
    if (quantity !== '') SKUData.quantity = quantity

    setFormPending(true)
    setFormError('')

    try {
      if (isNew) {
        await dispatch(createShopSKU(SKUData))
      } else {
        await dispatch(updateShopSKU(SKUData, id))
      }
      closeModal()
    } catch {
      setFormError(t('organisation:shared.serverError'))
    } finally {
      setFormPending(false)
    }
  }

  const deleteSKU = async () => {
    setFormError('')
    try {
      await dispatch(deleteShopSKU(sku.id, parseInt(getParentId())))
      closeConfirmModal()
      closeModal()
    } catch {
      setFormError(t('organisation:shared.serverError'))
      closeConfirmModal()
    }
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('common:validation.required')),
    cost: Yup.number().min(1).required(t('common:validation.required')),
  })

  return (
    <div
      className={`modal modal--organisation modal--service modal--sku ${skuId ? 'is-active' : ''}`}
    >
      <div className="modal-background" onClick={cancelServiceChanges} />
      <div className="modal-content">
        <div className="box">
          <header className="modal__header">
            <span>
              {isNew ? t('organisation:sku.modal.titleNew') : t('organisation:sku.modal.titleEdit')}
            </span>
            <button
              className="modal-close is-large"
              aria-label={t('organisation:shared.closeAriaLabel')}
              onClick={cancelServiceChanges}
            />
          </header>
          <Formik
            enableReinitialize
            initialValues={{
              id: sku?.id || '',
              name: sku?.name || '',
              cost: sku?.cost || '',
              duration: sku?.duration || '',
              quantity: sku?.quantity || 1,
            }}
            validationSchema={validationSchema}
            onSubmit={saveSKU}
          >
            {({ handleChange, handleSubmit, values, touched, errors, dirty, setFieldTouched }) => (
              <form className="modal__inner form" onSubmit={handleSubmit}>
                <div className="modal__section">
                  <div className="columns">
                    <div className="column is-9">
                      <TextField
                        name="name"
                        placeholder={t('organisation:sku.modal.name.placeholder')}
                        label={t('organisation:sku.modal.name.label')}
                        value={values.name}
                        onChange={handleChange}
                        isError={touched.name && !!errors.name}
                        onBlur={() => setFieldTouched('name')}
                      />
                    </div>
                    <div className="column is-3">
                      <TextField
                        type="number"
                        name="cost"
                        placeholder={t('organisation:sku.modal.cost.placeholder')}
                        label={t('organisation:sku.modal.cost.label')}
                        value={values.cost}
                        onChange={handleChange}
                        isError={touched.cost && !!errors.cost}
                        onBlur={() => setFieldTouched('cost')}
                        min={1}
                      />
                    </div>
                  </div>
                </div>

                {[
                  'Personal Training',
                  'Group Training',
                  'Massage Therapy',
                  'Beauty Services',
                  'Moving Services',
                  'Handyman Services',
                  'Pet Services',
                  'Home Services',
                ].includes(parentCategory) && (
                  <div className="modal__section">
                    <div className="columns">
                      <div className="column">
                        <TextField
                          type="number"
                          name="duration"
                          placeholder={t('organisation:sku.modal.duration.placeholder')}
                          label={t('organisation:sku.modal.duration.label')}
                          value={values.duration}
                          onChange={handleChange}
                          isError={touched.duration && !!errors.duration}
                          onBlur={() => setFieldTouched('duration')}
                        />
                      </div>
                      <div className="column">
                        <TextField
                          type="number"
                          name="quantity"
                          placeholder={t('organisation:sku.modal.quantity.placeholder')}
                          label={t('organisation:sku.modal.quantity.label')}
                          value={values.quantity}
                          onChange={handleChange}
                          isError={touched.quantity && !!errors.quantity}
                          onBlur={() => setFieldTouched('quantity')}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="modal__section modal__actions">
                  {!isNew && (
                    <span className="link" onClick={openConfirmModal}>
                      {t('organisation:sku.modal.delete')}
                    </span>
                  )}
                  <button
                    type="button"
                    className="button button-gray is-block"
                    onClick={cancelServiceChanges}
                  >
                    {t('common:actions.cancel')}
                  </button>
                  <button
                    type="submit"
                    className={`button button-primary is-block ${formPending ? 'is-loading' : ''}`}
                    disabled={formPending || !dirty}
                  >
                    {isNew ? t('organisation:shared.actions.create') : t('common:actions.save')}
                  </button>
                  <p className="help is-danger">{formError}</p>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
      <ConfirmPopup
        title={t('organisation:sku.modal.deleteConfirm.title')}
        cancelText={t('organisation:sku.modal.deleteConfirm.cancelText')}
        confirmText={t('organisation:sku.modal.deleteConfirm.confirmText')}
        isOpen={modalConfirmOpened}
        cancelCallback={closeConfirmModal}
        confirmCallback={deleteSKU}
      />
    </div>
  )
}

export default OrganisationSKUModal
